// The questions — T121 (29 Sep 2026), docs/design/questions-and-fit-2026-09-26.md § 3, amended by
// docs/design/matcher-2026-09-27.md § 2.
//
// Niv and Mona's meeting (26 Sep 2026): kadmon describes every person the same way, so that code can
// set a person against a Search. These are that description: the same questions for everyone, plus
// a set per kind of role. This file is the ONE list — every agent, screen, import and check reads it,
// and a test holds each to it (CLAUDE.md § Repo conventions). It is pure: no database, no network,
// so the brain's bundle imports it as `@vera/db/questions`.
//
// WHERE THE WORDS COME FROM. Mona's sheet, returned on 29 Sep 2026 (the design's § 3.6.1): her
// question and her "Why" for every row, her order, her must-have or nice-to-have, and whether a
// client may hear it. She and Natanel wrote their changes into the draft cells, so what differs
// from the 26–28 Sep draft is theirs: the city is asked first, working the same way is a must-have,
// AI in the work is a must-have in the commercial and back-office sets, and "flexibility" and
// "intensity" are reasons to move. The three kinds of role stay, all senior (Natanel, 29 Sep 2026;
// board roles get a set once Mona says what to ask a board candidate). An answer holds 90 days where
// it goes stale (Mona: "2-3 months").
//
// WHAT A KEY IS. Permanent: never renamed, never reused (§ 3.7). A retired question stays here,
// marked, its answers still shown and never asked. Every change bumps QUESTIONS_VERSION.
//
// WHAT IS NEVER A QUESTION (§ 3.5, and the test beside this file): a special category (health,
// beliefs, politics, orientation, ethnic origin, union membership, a criminal record), or a proxy
// for one — age, date of birth, sex or gender, marital status, children, nationality, a photo.
// Where someone may work is asked; which passport they hold is not. Years set a floor, never a
// ceiling. A language is asked as a working level.
//
// NEVER ASKED OF A PERSON (`priority: null`): the kind of role, which a recruiter or the
// Search sets, and — since `questions-v2` (T122, 30 Sep 2026) — their current pay. Natanel, told
// that the EU's pay transparency directive (2023/970, Art. 5(2)) bars an employer asking an
// applicant's pay history, that countries had to adopt it by 7 June 2026, and that whether it
// reaches a person a headhunter approaches is open: "Don't ask it". What they offer is still
// captured, and a recruiter enters it by hand.

import { canonicalJson } from './canonical.ts';

/** Bumped on every change to this file, like a prompt version (§ 3.7). */
export const QUESTIONS_VERSION = 'questions-v4';

/** `core` is everyone; the rest are Mona's kinds of role (§ 3.2). */
export const FAMILIES = ['core', 'engineering', 'commercial', 'operations'] as const;
export type Family = (typeof FAMILIES)[number];
/** The kinds of role a person or a Search can be: every family but `core`. */
export type RoleFamily = Exclude<Family, 'core'>;

/** T53e (30 Sep 2026): a word that names a kind of role — a stored Search's family, a request's. */
export const isRoleFamily = (value: unknown): value is RoleFamily =>
  typeof value === 'string' && value !== 'core' && (FAMILIES as readonly string[]).includes(value);

/** How the brain names each family (Mona's words: "back office"). */
export const FAMILY_LABEL: Readonly<Record<Family, string>> = {
  core: 'Everyone',
  engineering: 'Engineering',
  commercial: 'Commercial',
  operations: 'Back office',
};

/**
 * What a Search usually needs of a question, from Mona's column (matcher doc § 2): a new Search's
 * requirements start from it, and a recruiter changes them per Search.
 */
export type Usually = 'must' | 'nice' | 'depends' | 'never';

export type QuestionType =
  /** One option's key. */
  | 'one_of'
  /** Option keys; with `open`, any short name too ("a list we keep adding to"). */
  | 'many_of'
  /** A whole number of `unit`s, 0 or more, at most `max`. */
  | 'number'
  /** ISO 3166-1 alpha-2, upper case ("SE"). */
  | 'country'
  /** Several of `country`. */
  | 'countries'
  /** Short free text, for what no option list can hold (§ 3.5 rule 6). */
  | 'text'
  /** A month, `YYYY-MM`. */
  | 'month'
  /** Languages they work in: `[{ code, level? }]`, ISO 639-1 codes, the level when said (§ 3.5 rule 4). */
  | 'languages'
  /** No, within their country, or abroad to named countries: `{ scope, countries? }`. */
  | 'relocation'
  /** An amount, a currency, a period, base or total — kept in `candidates.pay`, never `answers`. */
  | 'money';

export interface QuestionOption {
  key: string;
  label: string;
  /** Only for these families; absent, for every family the question is in (`ai_use`, § 3.6). */
  families?: readonly RoleFamily[];
}

export interface Question {
  key: string;
  /** `core`, or the kinds of role it belongs to. */
  families: readonly Family[];
  /** How the brain labels it. */
  label: string;
  /** Mona's question, as she would ask it: the agent's hint, never a script. */
  ask: string;
  /** Mona's "Why". */
  why: string;
  type: QuestionType;
  /** For `one_of` and `many_of`, in her order. */
  options?: readonly QuestionOption[];
  /** `many_of` only: a name not among the options is kept too (a new framework, a new system). */
  open?: boolean;
  /** `number` only. */
  unit?: 'years' | 'people' | 'weeks';
  /** `number` only: the highest value that is not a typo. */
  max?: number;
  /**
   * Her asking order, 1 first. `null`: kadmon never asks it — a recruiter or the Search sets it.
   */
  priority: 1 | 2 | 3 | 4 | 5 | null;
  /** Whether client-vera may tell a client (§ 3.4). Money never. */
  client: boolean;
  usually: Usually;
  /** Days an answer holds before kadmon checks it again; null for what does not go stale. */
  freshFor: number | null;
  /** The QUESTIONS_VERSION that added it. */
  since: string;
  /** Kept for its answers, never asked, never required (§ 3.7). */
  retired?: boolean;
  /**
   * A document cannot settle it: the answer turns on how a line is read, and a clear one is the
   * person's own word. A CV or a LinkedIn is never read for it (`openSentQuestions`, T443); she asks.
   */
  judgement?: boolean;
  /**
   * T558: a list a person keeps adding to. Each thing they tell goes on top of what they said before
   * (`addToldNames`), and however long the list, each name is what the length floor reads
   * (`toldWordsMayGoOn`).
   */
  addsUp?: boolean;
}

/** Mona, 29 Sep 2026: an answer that goes stale is checked again after "2-3 months". */
const STALE_AFTER_DAYS = 90;

const V1 = 'questions-v1';
const V3 = 'questions-v3';
const V4 = 'questions-v4';
/** People at a company, in bands: § 5.4's, shared by a person's largest company and a client's size. */
const HEADCOUNT_BANDS: readonly QuestionOption[] = [
  { key: 'up_to_10', label: '1 to 10' },
  { key: 'up_to_50', label: '11 to 50' },
  { key: 'up_to_200', label: '51 to 200' },
  { key: 'up_to_1000', label: '201 to 1,000' },
  { key: 'over_1000', label: 'Over 1,000' },
];
const yesNo: readonly QuestionOption[] = [
  { key: 'yes', label: 'Yes' },
  { key: 'no', label: 'No' },
];

export const QUESTIONS: readonly Question[] = [
  // --- Everyone ------------------------------------------------------------------------------
  {
    key: 'role_family',
    families: ['core'],
    label: 'Kind of role',
    ask: 'Which kind of role is this person: engineering, commercial or back office?',
    why: 'Not asked of the candidate: a recruiter or the Search sets it. It decides which of the role sets apply to them.',
    type: 'one_of',
    options: [
      { key: 'engineering', label: 'Engineering' },
      { key: 'commercial', label: 'Commercial' },
      { key: 'operations', label: 'Back office' },
    ],
    priority: null,
    client: true,
    usually: 'never',
    freshFor: null,
    since: V1,
  },
  {
    key: 'country',
    families: ['core'],
    label: 'Country',
    ask: 'Which country do they live in?',
    why: 'To match them to roles in the right place, read together with whether they would move.',
    type: 'country',
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'city',
    families: ['core'],
    label: 'City',
    ask: 'Which city do they live in?',
    why: 'To tell whether they could get to the office, for on-site and hybrid roles.',
    type: 'text',
    priority: 1,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'relocation',
    families: ['core'],
    label: 'Would they move',
    ask: 'Would they move for the right job: not at all, within their own country, or abroad (and to which countries)?',
    why: 'To match them to roles away from where they live now.',
    type: 'relocation',
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'work_mode',
    families: ['core'],
    label: 'How they want to work',
    ask: 'Do they want to work in the office, hybrid or fully remote, or are they open to any?',
    why: 'To match them to roles that work the same way.',
    type: 'one_of',
    options: [
      { key: 'on_site', label: 'On site' },
      { key: 'hybrid', label: 'Hybrid' },
      { key: 'remote_only', label: 'Remote only' },
      { key: 'any', label: 'Any' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'right_to_work',
    families: ['core'],
    label: 'Can work without sponsorship in',
    ask: 'In which countries can they already work legally, without an employer sponsoring a visa?',
    why: 'To leave out roles they could not legally take. kadmon never asks for a nationality or a passport.',
    type: 'countries',
    priority: 2,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'seniority',
    families: ['core'],
    label: 'Level',
    ask: 'What level are they at now, from individual contributor up to C-level or founder?',
    why: 'To match them to roles at the right level.',
    type: 'one_of',
    options: [
      { key: 'individual_contributor', label: 'Individual contributor' },
      { key: 'lead', label: 'Lead' },
      { key: 'manager', label: 'Manager' },
      { key: 'head_of', label: 'Head of' },
      { key: 'director', label: 'Director' },
      { key: 'vp', label: 'VP' },
      { key: 'c_level', label: 'C-level' },
      { key: 'founder', label: 'Founder' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'years_experience',
    families: ['core'],
    label: 'Years in their field',
    ask: 'How many years have they worked in their field?',
    why: 'To check a role’s minimum. There is never a maximum: capping years would be age discrimination.',
    type: 'number',
    unit: 'years',
    max: 60,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'people_managed',
    families: ['core'],
    label: 'People they manage now',
    ask: 'How many people do they manage today, counting everyone under them? 0 if nobody.',
    why: 'To match them to roles that lead a team of a similar size.',
    type: 'number',
    unit: 'people',
    max: 100_000,
    priority: 2,
    client: true,
    usually: 'depends',
    freshFor: null,
    since: V1,
  },
  {
    key: 'search_status',
    families: ['core'],
    label: 'Where they stand',
    ask: 'How open are they to a move right now: actively looking, open to the right thing, or not looking?',
    why: 'Helps the recruiter judge timing and approach. Kept from clients because it is a negotiating position.',
    type: 'one_of',
    options: [
      { key: 'actively_looking', label: 'Actively looking' },
      { key: 'open', label: 'Open to the right thing' },
      { key: 'not_looking', label: 'Not looking' },
    ],
    priority: 1,
    client: false,
    usually: 'never',
    freshFor: STALE_AFTER_DAYS,
    since: V1,
  },
  {
    key: 'notice_weeks',
    families: ['core'],
    label: 'Notice period',
    ask: 'How many weeks’ notice must they give their current employer?',
    why: 'To tell a client how soon they could start.',
    type: 'number',
    unit: 'weeks',
    max: 104,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: STALE_AFTER_DAYS,
    since: V1,
  },
  {
    key: 'earliest_start',
    families: ['core'],
    label: 'Earliest start',
    ask: 'What is the earliest month they could start a new job?',
    why: 'Can be later than their notice, for example while they wait for a bonus. Tells a client when they are free.',
    type: 'month',
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: STALE_AFTER_DAYS,
    since: V1,
  },
  {
    key: 'languages',
    families: ['core'],
    label: 'Working languages',
    ask: 'Which languages can they work in, and at what level: working, fluent or native?',
    why: 'For roles where the job needs a language. Asked as a working level, never as where someone comes from.',
    type: 'languages',
    priority: 3,
    client: true,
    usually: 'depends',
    freshFor: null,
    since: V1,
  },
  {
    key: 'stage_wanted',
    families: ['core'],
    label: 'Company stage they want',
    ask: 'What stage of company do they want to join next: seed, growth, scale-up, corporate, or any?',
    why: 'To match them to clients at the stage they want.',
    type: 'one_of',
    options: [
      { key: 'seed', label: 'Seed' },
      { key: 'growth', label: 'Growth' },
      { key: 'scale_up', label: 'Scale-up' },
      { key: 'corporate', label: 'Corporate' },
      { key: 'any', label: 'Any' },
    ],
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'motivations',
    families: ['core'],
    label: 'What would make them move',
    ask: 'What would make them leave their current job? They can pick several reasons.',
    why: 'To pitch each role on what they care about, and to match them to roles that offer it.',
    type: 'many_of',
    options: [
      { key: 'scope', label: 'Scope' },
      { key: 'money', label: 'Money' },
      { key: 'title', label: 'Title' },
      { key: 'mission', label: 'Mission' },
      { key: 'product', label: 'Product' },
      { key: 'team', label: 'Team' },
      { key: 'equity', label: 'Equity' },
      { key: 'stability', label: 'Stability' },
      { key: 'remote', label: 'Remote' },
      { key: 'learning', label: 'Learning' },
      { key: 'flexibility', label: 'Flexibility' },
      { key: 'intensity', label: 'Intensity' },
    ],
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'sectors',
    families: ['core'],
    label: 'Sectors they know',
    ask: 'Which industries have they worked in?',
    why: 'To match them to clients in industries they already know.',
    type: 'many_of',
    options: [
      { key: 'fintech', label: 'FinTech' },
      { key: 'ai_infrastructure', label: 'AI infrastructure' },
      { key: 'enterprise_software', label: 'Enterprise software' },
      { key: 'saas', label: 'SaaS' },
      { key: 'cybersecurity', label: 'Cybersecurity' },
      { key: 'health_tech', label: 'Health tech' },
      { key: 'ecommerce', label: 'E-commerce and marketplaces' },
      { key: 'gaming', label: 'Gaming' },
      { key: 'climate_energy', label: 'Climate and energy' },
      { key: 'defence', label: 'Defence' },
      { key: 'deep_tech', label: 'Deep tech' },
      { key: 'consumer', label: 'Consumer' },
      { key: 'hardware', label: 'Hardware' },
      { key: 'other', label: 'Other' },
    ],
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'exits',
    families: ['core'],
    label: 'Been through an acquisition or an IPO',
    ask: 'Have they worked at a company when it was bought or went public (an IPO)?',
    why: 'Some clients want someone who has been through that before.',
    type: 'one_of',
    options: yesNo,
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'pay_current',
    families: ['core'],
    label: 'Current pay',
    ask: 'What are they paid now? The amount and currency, per year, month or day, and whether it is base salary or the total package.',
    why: 'Background for the recruiter’s negotiation. Never told to a client, and never asked by kadmon (questions-v2): noted when they offer it.',
    type: 'money',
    priority: null,
    client: false,
    usually: 'never',
    freshFor: null,
    since: V1,
  },
  {
    key: 'pay_expected',
    families: ['core'],
    label: 'Expected pay',
    ask: 'What pay would they need to take a new job? The same details as their current pay.',
    why: 'To check them against the role’s budget. Told to a client only when they ask, for a person put forward to them (T178a).',
    type: 'money',
    priority: 2,
    client: false,
    usually: 'must',
    freshFor: STALE_AFTER_DAYS,
    since: V1,
  },
  // --- Engineering ---------------------------------------------------------------------------
  {
    key: 'ai_depth',
    families: ['engineering'],
    label: 'AI experience',
    ask: 'How deep is their AI experience, from using AI tools every day up to building models or doing research?',
    why: 'To match them to roles that need a given depth of AI work.',
    type: 'one_of',
    options: [
      { key: 'none', label: 'None' },
      { key: 'uses_tools', label: 'Uses AI tools every day' },
      { key: 'shipped_features', label: 'Has shipped AI features' },
      { key: 'builds_models', label: 'Builds or fine-tunes models' },
      { key: 'research', label: 'Research' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'domain',
    families: ['engineering'],
    label: 'Main area',
    ask: 'Which area of engineering do they mainly work in, such as backend, mobile or data?',
    why: 'To match them to roles in the same area.',
    type: 'one_of',
    options: [
      { key: 'backend', label: 'Backend' },
      { key: 'frontend', label: 'Frontend' },
      { key: 'full_stack', label: 'Full stack' },
      { key: 'mobile', label: 'Mobile' },
      { key: 'data', label: 'Data' },
      { key: 'ml', label: 'ML' },
      { key: 'infrastructure', label: 'Infrastructure and platform' },
      { key: 'security', label: 'Security' },
      { key: 'embedded', label: 'Embedded' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'stack',
    families: ['engineering'],
    label: 'Languages and frameworks',
    ask: 'Which programming languages and frameworks do they work with?',
    why: 'To match them to clients using the same technology. The list grows as new ones come up.',
    type: 'many_of',
    open: true,
    options: [
      { key: 'python', label: 'Python' },
      { key: 'typescript', label: 'TypeScript' },
      { key: 'javascript', label: 'JavaScript' },
      { key: 'go', label: 'Go' },
      { key: 'rust', label: 'Rust' },
      { key: 'java', label: 'Java' },
      { key: 'kotlin', label: 'Kotlin' },
      { key: 'scala', label: 'Scala' },
      { key: 'csharp', label: 'C#' },
      { key: 'cpp', label: 'C++' },
      { key: 'c', label: 'C' },
      { key: 'swift', label: 'Swift' },
      { key: 'ruby', label: 'Ruby' },
      { key: 'php', label: 'PHP' },
      { key: 'react', label: 'React' },
      { key: 'node', label: 'Node.js' },
      { key: 'kubernetes', label: 'Kubernetes' },
    ],
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'cloud',
    families: ['engineering'],
    label: 'Clouds in production',
    ask: 'Which cloud platforms, or their own servers, have they run live products on?',
    why: 'To match them to clients on the same cloud.',
    type: 'many_of',
    options: [
      { key: 'aws', label: 'AWS' },
      { key: 'gcp', label: 'GCP' },
      { key: 'azure', label: 'Azure' },
      { key: 'on_premises', label: 'On premises' },
      { key: 'none', label: 'None' },
    ],
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'largest_team_led',
    families: ['engineering'],
    label: 'Largest engineering team led',
    ask: 'What is the largest engineering team they have ever led, in people?',
    why: 'Their biggest team ever, not today’s. For roles that will lead a team that size.',
    type: 'number',
    unit: 'people',
    max: 100_000,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'zero_to_one',
    families: ['engineering'],
    label: 'Built something from scratch',
    ask: 'Have they built a product or a team from scratch?',
    why: 'Early-stage clients need someone who can start things from nothing.',
    type: 'one_of',
    options: yesNo,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
    judgement: true,
  },
  {
    key: 'hands_on',
    families: ['engineering'],
    label: 'Still writes code',
    ask: 'Do they still write code themselves: every day, sometimes, or not any more?',
    why: 'For hands-on roles, such as a CTO who still codes.',
    type: 'one_of',
    options: [
      { key: 'every_day', label: 'Every day' },
      { key: 'sometimes', label: 'Sometimes' },
      { key: 'no', label: 'No' },
    ],
    priority: 2,
    client: true,
    usually: 'depends',
    freshFor: null,
    since: V1,
  },
  // --- Commercial and back office: one AI question, each set its own options (§ 3.6) ----------
  {
    key: 'ai_use',
    families: ['commercial', 'operations'],
    label: 'AI in their work',
    ask: 'How does AI show up in their work?',
    why: 'To match them to clients that want someone who works with AI.',
    type: 'one_of',
    options: [
      { key: 'none', label: 'None' },
      { key: 'uses_tools', label: 'Uses AI tools' },
      { key: 'sells_ai', label: 'Sells an AI product', families: ['commercial'] },
      { key: 'ai_in_selling', label: 'Builds AI into how they sell', families: ['commercial'] },
      { key: 'automates', label: 'Automates work with AI', families: ['operations'] },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  // --- Commercial ----------------------------------------------------------------------------
  {
    key: 'motion',
    families: ['commercial'],
    label: 'How they sell',
    ask: 'Who do they sell to, and how: self-serve, small and medium businesses, mid-market, large enterprises, or through partners?',
    why: 'Selling to enterprises is a different job from self-serve. This matches them to clients who sell the same way.',
    type: 'many_of',
    options: [
      { key: 'self_serve', label: 'Self-serve' },
      { key: 'smb', label: 'SMB' },
      { key: 'mid_market', label: 'Mid-market' },
      { key: 'enterprise', label: 'Enterprise' },
      { key: 'partnerships', label: 'Partnerships' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'deal_size',
    families: ['commercial'],
    label: 'Typical deal, in euros',
    ask: 'How big are the deals they usually close, in euros?',
    why: 'To match them to clients with deals of a similar size. It is the deals’ value, not their pay, so a client may hear it.',
    type: 'one_of',
    options: [
      { key: 'under_10k', label: 'Under 10k' },
      { key: 'to_50k', label: '10k to 50k' },
      { key: 'to_250k', label: '50k to 250k' },
      { key: 'to_1m', label: '250k to 1M' },
      { key: 'over_1m', label: 'Over 1M' },
    ],
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'quota_carried',
    families: ['commercial'],
    label: 'Carried a quota',
    ask: 'Have they had a personal sales target (a quota) they were measured against?',
    why: 'For roles where they will carry a target.',
    type: 'one_of',
    options: yesNo,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'territories',
    families: ['commercial'],
    label: 'Sold into',
    ask: 'Which countries have they sold into?',
    why: 'To match them to clients selling into the same markets.',
    type: 'countries',
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'team_built',
    families: ['commercial'],
    label: 'Built a sales team',
    ask: 'Have they hired and built up a sales team?',
    why: 'For roles that must build a team, not only sell.',
    type: 'one_of',
    options: yesNo,
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  // --- Back office ---------------------------------------------------------------------------
  {
    key: 'function',
    families: ['operations'],
    label: 'Function',
    ask: 'Which back office area do they work in: finance, accounting, people and HR, legal, operations or administration?',
    why: 'To match them to roles in the same area.',
    type: 'one_of',
    options: [
      { key: 'finance', label: 'Finance' },
      { key: 'accounting', label: 'Accounting' },
      { key: 'people', label: 'People and HR' },
      { key: 'legal', label: 'Legal' },
      { key: 'operations', label: 'Operations' },
      { key: 'administration', label: 'Administration' },
    ],
    priority: 1,
    client: true,
    usually: 'must',
    freshFor: null,
    since: V1,
  },
  {
    key: 'systems',
    families: ['operations'],
    label: 'Systems used every day',
    ask: 'Which software do they use every day at work, such as SAP, NetSuite or HiBob?',
    why: 'To match them to clients using the same systems. The list grows as new ones come up.',
    type: 'many_of',
    open: true,
    options: [
      { key: 'sap', label: 'SAP' },
      { key: 'netsuite', label: 'NetSuite' },
      { key: 'priority', label: 'Priority' },
      { key: 'salesforce', label: 'Salesforce' },
      { key: 'hibob', label: 'HiBob' },
      { key: 'workday', label: 'Workday' },
    ],
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'qualifications',
    families: ['operations'],
    label: 'Professional qualifications',
    ask: 'Which professional qualifications do they hold, such as CPA or ACCA, or admission to the bar?',
    why: 'For roles that require one, such as a qualified accountant or lawyer.',
    type: 'many_of',
    open: true,
    options: [
      { key: 'cpa', label: 'CPA' },
      { key: 'acca', label: 'ACCA' },
      { key: 'cima', label: 'CIMA' },
      { key: 'cipd', label: 'CIPD' },
      { key: 'bar', label: 'Admitted to the bar' },
    ],
    priority: 2,
    client: true,
    usually: 'depends',
    freshFor: null,
    since: V1,
  },
  {
    key: 'standards',
    families: ['operations'],
    label: 'Standards and regimes',
    ask: 'Which accounting standards, laws or compliance frameworks do they know well, such as IFRS, SOX or GDPR?',
    why: 'To match them to clients working under the same rules.',
    type: 'many_of',
    open: true,
    options: [
      { key: 'ifrs', label: 'IFRS' },
      { key: 'us_gaap', label: 'US GAAP' },
      { key: 'sox', label: 'SOX' },
      { key: 'iso_27001', label: 'ISO 27001' },
      { key: 'gdpr', label: 'GDPR' },
    ],
    priority: 3,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  {
    key: 'largest_company',
    families: ['operations'],
    label: 'Largest company they ran it for',
    ask: 'What is the largest company, by number of employees, they have run this function for?',
    why: 'To match them to clients of a similar size.',
    type: 'one_of',
    options: HEADCOUNT_BANDS,
    priority: 2,
    client: true,
    usually: 'nice',
    freshFor: null,
    since: V1,
  },
  // T558 (Natanel, 2 Nov 2026, his T536 words: "all comapnies th person lists should be automatically
  // recorded"): the companies they name open roles to them that nobody else is offered yet. Never
  // read from a CV or a LinkedIn (`judgement`): where they worked is not where they want to.
  {
    key: 'ideal_companies',
    families: ['core'],
    label: "Companies they'd ideally work at",
    ask: 'Which companies would they ideally like to work at?',
    why: 'Their list of roles starts with these, and kadmon adds any it does not know to its companies.',
    type: 'many_of',
    open: true,
    options: [],
    priority: 3,
    client: false,
    usually: 'never',
    freshFor: null,
    since: V4,
    judgement: true,
    addsUp: true,
  },
];

/**
 * T518 (Natanel, 28 Oct 2026: "The 6-7 essentials"): what kadmon needs before it looks for roles
 * for a person, and, with the thread Search's own lines, before the team is asked to put them
 * forward: the kind of role, the level, where they live and whether they would move, why they would
 * move, money, notice. The rest of the list is asked after, at natural moments, the question that
 * opens the most roles first (`gaps.ts`).
 */
export const ESSENTIAL: ReadonlySet<string> = new Set([
  'role_family',
  'seniority',
  'country',
  'city',
  'relocation',
  'motivations',
  'pay_expected',
  'notice_weeks',
  'ideal_companies',
]);

/**
 * T558 (Natanel, 2 Nov 2026: "before the first list"): the essentials that hold the look alone and
 * never the team's put-forward ask (`holdingGaps`, gaps.ts): which companies a person would like
 * shapes their list and is nothing the team needs to put them forward.
 */
export const LOOK_ONLY: ReadonlySet<string> = new Set(['ideal_companies']);

/**
 * T291 (5 Oct 2026): what candidate-vera captures under a question's key when the person would
 * rather not say. Natanel: "refusals mean that question was answered with a refusal": it counts as
 * answered for the meeting ask and is never asked again on the thread, and it never lands on their
 * card as an answer (`toldAnswerFrom` reads it as none).
 */
export const DECLINED = 'declined';

/**
 * Whether a captured value is a refusal: `declined` as her note asks, or the words a model writes
 * instead ("declined to say", "rather not say", "prefers not to share"). The code review of T291:
 * an exact match let "declined to say" onto the card as a free-text answer.
 */
export function isDeclined(value: string): boolean {
  const said = value
    .trim()
    .toLowerCase()
    .replace(/[’']/g, "'")
    .replace(/[.!]+$/, '');
  return (
    said === DECLINED ||
    /^declined to (say|answer|share|tell)\b/.test(said) ||
    /^(they |he |she |would )?(would )?(rather|prefers?|preferred) not (to )?(say|answer|share|tell|get into|discuss)\b/.test(
      said,
    ) ||
    DECLINED_SV_HE.test(said)
  );
}

/**
 * T373b: the same refusals in Swedish and Hebrew ("vill helst inte säga", "מעדיף שלא לומר"). Refusals
 * only, the safe direction: a match keeps a value off the card. Unicode boundaries, never `\b`.
 */
const DECLINED_SV_HE =
  /^(?:(?:de|han|hon|hen) )?(?:(?:vill helst|föredrar att|vill) inte (?:säga|svara|dela|berätta)|avböj(?:de|er) att (?:svara|säga|dela|berätta)|(?:סירבה?|סירבו) (?:לענות|לומר|לשתף)|(?:מעדיף|מעדיפה) שלא (?:לומר|לענות|לשתף)|לא רוצה לשתף)(?![\p{L}\p{N}])/u;

/**
 * T291 (Natanel, "Yes, in T291"): what code records under a question kadmon asked twice and the
 * person wrote past without answering. Like a refusal it answers the question for the meeting ask,
 * is not asked again until it is stale, and never lands on their card as a value.
 */
export const IGNORED = 'ignored';

/**
 * T291 (Natanel, 5 Oct 2026: "Flag it, fixed wording"): the label of the line code adds to the
 * team's meeting ask while the person's kind of role is on neither their card nor the Search, so
 * its own questions may not all have been asked. One home: the brain's card and the WhatsApp ask.
 */
export const ROLE_NOT_ON_CARD = 'Kind of role not on their card yet';

/** T291: a value that answers a question without being an answer to put on the card. */
export const isNonAnswer = (value: string): boolean =>
  isDeclined(value) || value.trim().toLowerCase() === IGNORED;

/** The two money questions: their answers live in `candidates.pay`, which a grant can withhold. */
export const PAY_KEYS = ['pay_current', 'pay_expected'] as const;
export type PayKey = (typeof PAY_KEYS)[number];
export const isPayKey = (key: string): key is PayKey =>
  (PAY_KEYS as readonly string[]).includes(key);

const BY_KEY: ReadonlyMap<string, Question> = new Map(QUESTIONS.map((q) => [q.key, q]));

/** What a question's answer looks like, for a refusal the model can act on. */
export function expectedAnswer(q: Question): string {
  const options = (q.options ?? []).map((o) => o.label).join(', ');
  switch (q.type) {
    case 'one_of':
      return `one of ${options}`;
    case 'many_of':
      if (!options) return 'one or more names';
      return `one or more of ${options}${q.open ? ', or other names' : ''}`;
    case 'number':
      return `a whole number${q.unit ? ` of ${q.unit}` : ''}`;
    case 'country':
      return 'a country, by name or code';
    case 'countries':
      return 'countries, by name or code, separated by commas';
    case 'text':
      return 'a few words';
    case 'month':
      return 'a month, like 2027-01 or Jan 2027';
    case 'languages':
      return 'languages with a level, like "Swedish (native), English"';
    case 'relocation':
      return 'no, within their country, or yes and where';
    case 'money':
      return 'an amount with its currency, per year, month or day, and base or total, like "EUR 150k base per year"';
  }
}

/**
 * T202, small item 6: the refusal for text that is not an answer to a question, in one place so
 * the brain's `set_answers` and the evals' handler of it say the same thing.
 */
export const notAnAnswer = (q: Question, text: string): string =>
  `"${text}" is not an answer to ${q.label}: it takes ${expectedAnswer(q)}`;

export function questionByKey(key: string): Question | undefined {
  return BY_KEY.get(key);
}

/**
 * The questions a person's families call for, in asking order (§ 3.2): core, plus each family
 * given. Retired questions are left out; `pay` questions are in (the brain shows them, marked).
 */
export function questionsFor(families: readonly RoleFamily[]): Question[] {
  const wanted = new Set<Family>(['core', ...families]);
  return QUESTIONS.filter(
    (q) => !q.retired && q.families.some((family) => wanted.has(family)),
  ).sort((a, b) => (a.priority ?? 6) - (b.priority ?? 6));
}

/**
 * The questions a Search's requirements may name for a kind of role, in Mona's asking order — the
 * Requirements card's rows (T53e). Never pay (the band is its own field), never a question Mona
 * marked `never`, never the two shapes a requirement cannot hold (relocation, money). One home
 * since T126a: a rejection's questions are offered from the same set (`rejectKeyOffer`).
 */
export function requirementQuestions(family: RoleFamily | '' | null): Question[] {
  return questionsFor(family ? [family] : []).filter(
    (q) =>
      !isPayKey(q.key) && q.usually !== 'never' && q.type !== 'relocation' && q.type !== 'money',
  );
}

/** A question's options for one family (`ai_use`'s differ between commercial and back office). */
export function optionsFor(question: Question, family?: RoleFamily): QuestionOption[] {
  return (question.options ?? []).filter(
    (option) => !option.families || family === undefined || option.families.includes(family),
  );
}

// --- Answers ----------------------------------------------------------------------------------

/** Where an answer came from (§ 3.3). */
export const ANSWER_SOURCES = ['recruiter', 'told', 'provider', 'import', 'derived'] as const;
export type AnswerSource = (typeof ANSWER_SOURCES)[number];

/**
 * Which answer stands (§ 3.3): the person's own words and a recruiter's entry outrank a provider,
 * an import or a parser, whatever their dates; within one rank the newest wins.
 */
export const SOURCE_RANK: Readonly<Record<AnswerSource, number>> = {
  recruiter: 2,
  told: 2,
  provider: 1,
  import: 1,
  derived: 1,
};

export type Relocation = { scope: 'no' | 'within_country' | 'abroad'; countries?: string[] };
export type LanguageLevel = 'working' | 'fluent' | 'native';
/**
 * A language and the level they work in it. T202c (2 Oct 2026): `level` only when someone said it;
 * a language listed with none keeps none, never a guessed "working" shown as known (fit.ts reads a
 * listed language as at least working, T121a's "they listed it", and unknown above that).
 */
export type Language = { code: string; level?: LanguageLevel };
export type Money = {
  amount: number;
  currency: string;
  period: 'year' | 'month' | 'day';
  includes: 'base' | 'total';
};
export type AnswerValue = string | string[] | number | Relocation | Language[] | Money;

/** One stored answer: the value and where it came from. `alt` keeps a lower-ranked disagreement. */
export interface StoredAnswer {
  value: AnswerValue;
  source: AnswerSource;
  /** ISO date-time the answer was set. */
  at: string;
  /** The staff member's name, when a person set it. */
  by?: string;
  /** The captured fact, entry request or provider record it came from. */
  ref?: string;
  /** Answers it replaced or that wait beside it, newest first, for a recruiter to take or leave. */
  alt?: AltAnswer[];
  /** T569: read from a CV or LinkedIn the reader was not sure of; always `source: 'provider'`. */
  unsure?: Unsure;
}

/**
 * T569 (30 Oct 2026, Natanel: "Fill all automatically, mark unsure ones"): an answer a CV or
 * LinkedIn read was only fairly sure of, with the line it was read from. It fills the card and
 * closes its question, ranks under any sure answer (which replaces it, keeping nothing in `alt`),
 * never replaces a standing answer, never rules anyone out, and is never said to the person.
 */
export interface Unsure {
  from: 'cv' | 'linkedin';
  quote: string;
}

/** The longest `Unsure.quote` kept: the line the Questions card shows under the answer. */
export const UNSURE_QUOTE_MAX = 300;

/** An answer kept beside the standing one: who set it (`by`) and which message or record (`ref`). */
export interface AltAnswer {
  value: AnswerValue;
  source: AnswerSource;
  at: string;
  by?: string;
  ref?: string;
}

/** An answer as `alt` keeps it: the value and where it came from, nothing else. */
function altOf(a: StoredAnswer): AltAnswer {
  return {
    value: a.value,
    source: a.source,
    at: a.at,
    ...(a.by ? { by: a.by } : {}),
    ...(a.ref ? { ref: a.ref } : {}),
  };
}

/** `candidates.answers` and `candidates.pay`: key → stored answer. */
export type Answers = Record<string, StoredAnswer>;

/** One answer as an entry request carries it: the worker stamps `at` when it applies it. */
export interface AnswerSet {
  value: AnswerValue;
  source: AnswerSource;
  by?: string;
  ref?: string;
  unsure?: Unsure;
}

/** What an entry request carries for `answers` or `pay`: key → the answer to set, or null to clear it. */
export type AnswerPatch = Record<string, AnswerSet | null>;

/**
 * An entry request's answers on top of what the record holds (§ 3.3), as the worker applies them:
 * each set answer merged by rank (`mergeAnswer`), each null a cleared answer. `at` is the moment
 * the worker applies it. Keys the patch does not name are left exactly as they were.
 */
export function applyAnswerPatch(standing: unknown, patch: AnswerPatch, at: string): Answers {
  const out: Answers = { ...((standing ?? {}) as Answers) };
  for (const [key, set] of Object.entries(patch)) {
    if (set === null) {
      delete out[key];
      continue;
    }
    out[key] = mergeAnswer(out[key], { ...set, at });
  }
  return out;
}

/** ISO 3166-1 alpha-2, as of 2026, plus XK (Kosovo, in wide use). */
export const COUNTRY_CODES: ReadonlySet<string> = new Set(
  (
    'AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ ' +
    'BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR ' +
    'CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR ' +
    'GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ' +
    'ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ ' +
    'LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ ' +
    'MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF ' +
    'PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI ' +
    'SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR ' +
    'TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS XK YE YT ZA ZM ZW'
  ).split(' '),
);

/** ISO 639-1. */
export const LANGUAGE_CODES: ReadonlySet<string> = new Set(
  (
    'aa ab ae af ak am an ar as av ay az ba be bg bh bi bm bn bo br bs ca ce ch co cr cs cu cv ' +
    'cy da de dv dz ee el en eo es et eu fa ff fi fj fo fr fy ga gd gl gn gu gv ha he hi ho hr ' +
    'ht hu hy hz ia id ie ig ii ik io is it iu ja jv ka kg ki kj kk kl km kn ko kr ks ku kv kw ' +
    'ky la lb lg li ln lo lt lu lv mg mh mi mk ml mn mr ms mt my na nb nd ne ng nl nn no nr nv ' +
    'ny oc oj om or os pa pi pl ps pt qu rm rn ro ru rw sa sc sd se sg si sk sl sm sn so sq sr ' +
    'ss st su sv sw ta te tg th ti tk tl tn to tr ts tt tw ty ug uk ur uz ve vi vo wa wo xh yi ' +
    'yo za zh zu'
  ).split(' '),
);

/** Currencies a record may name: ISO 4217's, as the runtime knows them, and the ones kadmon sees. */
const CURRENCIES: ReadonlySet<string> = new Set([
  ...((Intl as unknown as { supportedValuesOf?: (key: string) => string[] }).supportedValuesOf?.(
    'currency',
  ) ?? []),
  'EUR',
  'USD',
  'GBP',
  'SEK',
  'NOK',
  'DKK',
  'CHF',
  'ILS',
  'PLN',
]);

const LEVELS: ReadonlySet<string> = new Set(['working', 'fluent', 'native']);
/** The longest free-text answer, and a text requirement's line. */
export const TEXT_MAX = 200;
export const OPEN_NAME_MAX = 60;

export type Parsed<T> = { ok: true; value: T } | { ok: false; reason: string };

/**
 * What checking or describing a value reads of its question: a person's question or, since T123,
 * a company's (`CompanyQuestion`, whose own type `offices` is checked by `parseCompanyAnswer`).
 */
export type AnswerShape = Pick<
  Question,
  'type' | 'label' | 'options' | 'open' | 'max' | 'unit' | 'addsUp'
>;
const ok = <T>(value: T): Parsed<T> => ({ ok: true, value });
const no = <T>(reason: string): Parsed<T> => ({ ok: false, reason });

/** "uk" and "UK" are how people write GB; everything else must be a code as it stands. */
function countryOf(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const code = raw.trim().toUpperCase();
  const normal = code === 'UK' ? 'GB' : code;
  return COUNTRY_CODES.has(normal) ? normal : null;
}

function optionOf(question: AnswerShape, raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const wanted = raw.trim().toLowerCase();
  const found = (question.options ?? []).find(
    (option) => option.key === wanted || option.label.toLowerCase() === wanted,
  );
  return found?.key ?? null;
}

const unique = <T>(values: T[]): T[] => [...new Set(values)];

/**
 * A value checked against its question: the stored form, or why it cannot be one (§ 3.3's
 * validation in code). An option may be given by its key or its label; a country as its code; an
 * open list keeps a name it does not know, trimmed.
 */
export function parseAnswerValue(question: AnswerShape, raw: unknown): Parsed<AnswerValue> {
  switch (question.type) {
    case 'one_of': {
      const key = optionOf(question, raw);
      return key ? ok(key) : no(`not one of ${question.label}'s options`);
    }
    case 'many_of': {
      if (!Array.isArray(raw) || raw.length === 0) return no('a list with at least one value');
      const values: string[] = [];
      for (const item of raw) {
        const key = optionOf(question, item);
        if (key) values.push(key);
        else if (question.open && typeof item === 'string') {
          const name = item.trim();
          if (!name || name.length > OPEN_NAME_MAX) return no(`"${item}" is not a short name`);
          values.push(name);
        } else return no(`"${String(item)}" is not one of ${question.label}'s options`);
      }
      return ok(unique(values));
    }
    case 'number': {
      const n = typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : raw;
      if (typeof n !== 'number' || !Number.isInteger(n) || n < 0)
        return no('a whole number, 0 or more');
      if (question.max !== undefined && n > question.max) return no(`at most ${question.max}`);
      return ok(n);
    }
    case 'country': {
      const code = countryOf(raw);
      return code ? ok(code) : no('a country code, such as SE or GB');
    }
    case 'countries': {
      if (!Array.isArray(raw) || raw.length === 0) return no('a list of country codes');
      const codes = raw.map(countryOf);
      if (codes.some((c) => c === null)) return no('a list of country codes, such as SE or GB');
      return ok(unique(codes as string[]));
    }
    case 'text': {
      if (typeof raw !== 'string' || !raw.trim()) return no('some text');
      const text = raw.trim();
      return text.length <= TEXT_MAX ? ok(text) : no(`at most ${TEXT_MAX} characters`);
    }
    case 'month': {
      if (typeof raw !== 'string' || !/^\d{4}-(0[1-9]|1[0-2])$/.test(raw.trim())) {
        return no('a month, such as 2027-01');
      }
      return ok(raw.trim());
    }
    case 'languages': {
      if (!Array.isArray(raw) || raw.length === 0) return no('a list of languages with levels');
      const languages: Language[] = [];
      for (const item of raw) {
        const entry = item as { code?: unknown; level?: unknown } | null;
        const code = typeof entry?.code === 'string' ? entry.code.trim().toLowerCase() : '';
        const level = typeof entry?.level === 'string' ? entry.level.trim().toLowerCase() : '';
        if (!LANGUAGE_CODES.has(code))
          return no(`"${String(entry?.code)}" is not a language code, such as sv`);
        if (level === '') {
          languages.push({ code });
          continue;
        }
        if (!LEVELS.has(level)) return no('each language at working, fluent or native level');
        languages.push({ code, level: level as LanguageLevel });
      }
      const byCode = new Map(languages.map((l) => [l.code, l]));
      return ok([...byCode.values()]);
    }
    case 'relocation': {
      const entry = raw as { scope?: unknown; countries?: unknown } | null;
      const scope = typeof entry?.scope === 'string' ? entry.scope : '';
      if (scope === 'no' || scope === 'within_country') return ok({ scope });
      if (scope !== 'abroad') return no('no, within their country, or abroad');
      if (entry?.countries === undefined) return ok({ scope });
      const parsed = parseAnswerValue({ ...question, type: 'countries' }, entry.countries);
      return parsed.ok ? ok({ scope, countries: parsed.value as string[] }) : parsed;
    }
    case 'money': {
      const m = raw as Partial<Record<keyof Money, unknown>> | null;
      const amount = typeof m?.amount === 'string' ? Number(m.amount) : m?.amount;
      if (typeof amount !== 'number' || !Number.isFinite(amount) || amount <= 0) {
        return no('an amount above 0');
      }
      const currency = typeof m?.currency === 'string' ? m.currency.trim().toUpperCase() : '';
      if (!/^[A-Z]{3}$/.test(currency) || !CURRENCIES.has(currency)) {
        return no('an ISO 4217 currency, such as EUR or SEK');
      }
      if (m?.period !== 'year' && m?.period !== 'month' && m?.period !== 'day') {
        return no('per year, month or day');
      }
      if (m?.includes !== 'base' && m?.includes !== 'total') return no('base or total');
      return ok({ amount, currency, period: m.period, includes: m.includes });
    }
  }
}

/** Relocation's three answers, for a screen's picker and a filter. */
export const RELOCATION_SCOPES: readonly QuestionOption[] = [
  { key: 'no', label: 'No' },
  { key: 'within_country', label: 'Within their country' },
  { key: 'abroad', label: 'Abroad' },
];

/**
 * The questions the Candidates list filters by (§ 3.8), in the order its filter bar offers them:
 * the ones a recruiter narrows a market by, each an option list, a country or relocation's scope.
 */
export const ANSWER_FILTERS = [
  'role_family',
  'country',
  'relocation',
  'work_mode',
  'seniority',
  'search_status',
  'ai_depth',
  'domain',
  'ai_use',
  'function',
  'motion',
] as const;

/**
 * A filter's value, checked: an option's key (or its label), a country code, or relocation's scope
 * — or null, for a question the list does not filter by or a value it does not take. A stale link
 * therefore narrows nothing rather than failing.
 */
export function answerFilterValue(key: string, raw: string): string | null {
  const question = questionByKey(key);
  if (!question || !(ANSWER_FILTERS as readonly string[]).includes(key)) return null;
  if (question.type === 'relocation') {
    return RELOCATION_SCOPES.some((scope) => scope.key === raw) ? raw : null;
  }
  if (question.type === 'country' || question.type === 'countries') return countryOf(raw);
  return optionOf(question, raw);
}

/**
 * Two values the same answer: order does not matter in a list, nor in an object's keys — T202c
 * (2 Oct 2026): jsonb hands pay back in its own key order, and an identical pay compared unequal,
 * so the card offered "Use it" under the answer it already showed.
 */
export function sameAnswerValue(a: AnswerValue, b: AnswerValue): boolean {
  return canonicalJson(a, { sortLists: true }) === canonicalJson(b, { sortLists: true });
}

/**
 * T202c (2 Oct 2026): the sources whose repeat of the same value is news — a person saying it again
 * or a recruiter entering it again checks it now (T202.md: #558 F8's "they just said it again");
 * an import, a research file or the record saying it again does not, or a re-run would clear
 * "check again" with nobody having checked.
 */
const REPEAT_REFRESHES: ReadonlySet<AnswerSource> = new Set(['told', 'recruiter']);

/**
 * An answer set on top of what stands (§ 3.3). One of equal or higher rank replaces it (the one it
 * replaces survives in the entry request that set it); one of lower rank that disagrees is kept in
 * `alt` and leaves the standing answer alone; one of lower rank that agrees changes nothing.
 *
 * T202c (2 Oct 2026): a lower-ranked answer the new one replaces is kept in `alt` too when it
 * disagrees — the card's words, "the provider's stays beside it", held in one order only — and the
 * same value said again at the same rank changes nothing unless a person or a recruiter said it
 * (`REPEAT_REFRESHES`).
 *
 * T569 (30 Oct 2026): an unsure answer ({@link Unsure}) only fills an empty one, and any sure
 * answer replaces it, the same value from the same source included, keeping nothing in `alt`.
 */
export function mergeAnswer(standing: StoredAnswer | undefined, next: StoredAnswer): StoredAnswer {
  const rankOf = (a: StoredAnswer) => SOURCE_RANK[a.source];
  const bare: StoredAnswer = {
    value: next.value,
    source: next.source,
    at: next.at,
    ...(next.by ? { by: next.by } : {}),
    ...(next.ref ? { ref: next.ref } : {}),
    ...(next.unsure ? { unsure: next.unsure } : {}),
  };
  if (!standing) return bare;
  if (next.unsure) return standing;
  if (standing.unsure) return bare;
  if (
    rankOf(next) === rankOf(standing) &&
    sameAnswerValue(standing.value, next.value) &&
    !REPEAT_REFRESHES.has(next.source)
  ) {
    // Its date stays. A record-read answer names the field it now follows (a level moving from the
    // title to the level field), or that reading is filed again on every save; an import's or
    // research's keeps the file or record that set it at its date (T202c review). T520a: an answer
    // read from a CV the team gave has no `ref`, so the same answer from a bought record gives it its.
    if (next.ref && standing.source === next.source) {
      if (next.source === 'derived' && next.ref !== standing.ref) {
        return { ...standing, ref: next.ref };
      }
      if (next.source === 'provider' && !standing.ref) return { ...standing, ref: next.ref };
    }
    return standing;
  }
  if (rankOf(next) >= rankOf(standing)) {
    // What a lower rank said stays beside the new answer while it still disagrees with it: what
    // stood before, when it was lower, and what stood beside it.
    // T431a (24 Oct 2026): a person and a recruiter rank alike, and either replacing the other keeps
    // the one it replaced (a rank-1 swap must not, or a re-run of `derivedPatch` on every save
    // grows `alt`); the same source replacing itself keeps nothing, as before.
    const before =
      rankOf(standing) < rankOf(next) || (rankOf(standing) === 2 && standing.source !== next.source)
        ? [altOf(standing)]
        : [];
    const alt = [...before, ...(standing.alt ?? [])]
      .filter((a) => !sameAnswerValue(a.value, next.value))
      .filter((a, i, all) => all.findIndex((b) => sameAnswerValue(a.value, b.value)) === i)
      .slice(0, 5);
    return alt.length > 0 ? { ...bare, alt } : bare;
  }
  if (sameAnswerValue(standing.value, next.value)) return standing;
  const alt = [
    altOf(next),
    ...(standing.alt ?? []).filter((a) => !sameAnswerValue(a.value, next.value)),
  ].slice(0, 5);
  return { ...standing, alt };
}

/**
 * T122 (30 Sep 2026): a person's own clear answer, put straight onto their card by the worker.
 * T431a (24 Oct 2026, Natanel: "answers people give go onto the profile by themselves"): it merges
 * like any answer ({@link mergeAnswer}) and **wins over a recruiter's own entry too** — the
 * recruiter's goes to `alt` with their name, where the card offers it with **Use it** — with two
 * limits that keep a recruiter's fix from being undone by a stale message:
 *
 * - **Older loses:** a told answer older than the recruiter's standing one waits in `alt` (a late
 *   read job, a redelivery).
 * - **The overrule sticks** (Natanel, 24 Oct): when the recruiter's `alt` already holds this told
 *   value from before their answer — they overruled it with Use it or an edit — the person repeating
 *   it leaves the recruiter's on top and only refreshes the entry's message. A different new answer
 *   still wins.
 */
export function mergeTold(standing: StoredAnswer | undefined, told: StoredAnswer): StoredAnswer {
  if (standing?.source === 'recruiter') {
    const same = sameAnswerValue(standing.value, told.value);
    if (Date.parse(told.at) < Date.parse(standing.at)) {
      if (same) return standing;
      const alt = [
        altOf(told),
        ...(standing.alt ?? []).filter((a) => !sameAnswerValue(a.value, told.value)),
      ].slice(0, 5);
      return { ...standing, alt };
    }
    // Overruled = the told value went to `alt` when the recruiter's answer replaced it, so it is older
    // than theirs; one that waited there after it was never acted on and wins when repeated.
    const overruled = (a: AltAnswer) =>
      a.source === 'told' &&
      sameAnswerValue(a.value, told.value) &&
      Date.parse(a.at) < Date.parse(standing.at);
    if (!same && (standing.alt ?? []).some(overruled)) {
      // Only the message link is refreshed: the date stays, so the next repeat is held the same way.
      const alt = (standing.alt ?? []).map((a) =>
        overruled(a) ? { ...a, ...(told.ref ? { ref: told.ref } : {}) } : a,
      );
      return { ...standing, alt };
    }
  }
  // T202c (2 Oct 2026): the person saying what the recruiter entered checks it now; left as it
  // stood, a stale answer stayed stale, and every later thread asked them to confirm it again.
  return mergeAnswer(standing, told);
}

/**
 * T558: an adding list (`addsUp`) after a person names `names`: what it held, older first, then what
 * is new, the same company once by `keyOf` (`employerKey`: "Spotify" is "Spotify AB"), or null when
 * nothing is new. Read name by name, so the overrule sticks here as it does for a whole answer in
 * {@link mergeTold}: a name a recruiter took off stays off when the person repeats it. Their
 * answer's date is later than the told list it replaced, which `alt` keeps, also once a new name has
 * gone on top of it. Pure, and handed `keyOf` as the employer key lives in research-vera.
 */
export function addToldNames(
  standing: StoredAnswer | undefined,
  names: readonly string[],
  keyOf: (name: string) => string | null,
): string[] | null {
  const key = (name: string) => keyOf(name) ?? name.trim().toLowerCase();
  const list = (value: AnswerValue | undefined): string[] =>
    Array.isArray(value) ? value.filter((item): item is string => typeof item === 'string') : [];
  const held = list(standing?.value);
  const seen = new Set(held.map(key));
  const beside = [...(standing ? [standing] : []), ...(standing?.alt ?? [])];
  const removed = new Set(
    beside
      .filter((answer) => answer.source === 'recruiter')
      .flatMap((recruiter) => {
        const kept = new Set(list(recruiter.value).map(key));
        return beside
          .filter((a) => a.source === 'told' && Date.parse(a.at) < Date.parse(recruiter.at))
          .flatMap((a) => list(a.value).map(key))
          .filter((name) => !kept.has(name));
      }),
  );
  const added: string[] = [];
  for (const name of names) {
    const at = key(name);
    if (seen.has(at) || removed.has(at)) continue;
    seen.add(at);
    added.push(name);
  }
  return added.length > 0 ? [...held, ...added] : null;
}

/**
 * T121b (29 Sep 2026): where an answer came from, in the Questions card's words, for an agent that
 * reads it: "told kadmon", the recruiter's name, "research", "an import", "read from the record".
 */
export function answerSource(answer: Pick<StoredAnswer, 'source' | 'by' | 'unsure'>): string {
  // T569: a guess is named as one wherever an agent reads it (the roster, fit's reasons).
  if (answer.unsure) {
    return `unsure, read from their ${answer.unsure.from === 'cv' ? 'CV' : 'LinkedIn'}`;
  }
  switch (answer.source) {
    case 'told':
      return 'told kadmon';
    case 'recruiter':
      return answer.by ?? 'a recruiter';
    case 'provider':
      return 'research';
    case 'import':
      return 'an import';
    case 'derived':
      return 'read from the record';
  }
}

/**
 * Whether an answer is due to be checked again (§ 3.1 `freshFor`). One whose date cannot be read
 * is (T189, 30 Sep 2026): nothing shows it still holds. Every writer stamps `at`, so only a
 * malformed row meets this, and then kadmon asks again and a client never hears it as current.
 */
export function isStale(question: Question, answer: StoredAnswer, now: Date): boolean {
  if (question.freshFor === null) return false;
  const at = Date.parse(answer.at);
  return !Number.isFinite(at) || now.getTime() - at > question.freshFor * 86_400_000;
}

/**
 * One stored answer in words, as the brain reads it (T121d, 3 Oct 2026): what it is, who said it
 * and the day, "due a check" past its `freshFor`, and each answer that disagrees. Console-vera's
 * `read_person` and her voice's lookup both say an answer so, so the two doors never differ.
 */
export function answerInWords(question: Question, answer: StoredAnswer, now: Date): string {
  const day = answer.at ? answer.at.slice(0, 10) : 'never';
  let words = `${describeAnswer(question, answer.value)} (${answerSource(answer)}, ${day})`;
  if (isStale(question, answer, now)) words += ', due a check';
  for (const alt of answer.alt ?? []) {
    words += `; ${answerSource(alt)} says ${describeAnswer(question, alt.value)}`;
  }
  return words;
}

const regionNames = new Intl.DisplayNames(['en'], { type: 'region' });
const languageNames = new Intl.DisplayNames(['en'], { type: 'language' });
/** "SE" → "Sweden", "sv" → "Swedish": an answer is read by people, and a reason quotes it. */
const countryName = (code: string): string => regionNames.of(code) ?? code;
const languageName = (code: string): string => languageNames.of(code) ?? code;

/** A value as a person reads it, from the question's own labels and the names of places. */
export function describeAnswer(question: AnswerShape, value: AnswerValue): string {
  const label = (key: string) => question.options?.find((o) => o.key === key)?.label ?? key;
  switch (question.type) {
    case 'one_of':
      return label(value as string);
    case 'many_of':
      return (value as string[]).map(label).join(', ');
    case 'number':
      return `${value as number}${question.unit ? ` ${question.unit}` : ''}`;
    case 'country':
      return countryName(value as string);
    case 'countries':
      return (value as string[]).map(countryName).join(', ');
    case 'text':
    case 'month':
      return value as string;
    case 'languages':
      return (value as Language[])
        .map((l) => `${languageName(l.code)}${l.level ? ` (${l.level})` : ''}`)
        .join(', ');
    case 'relocation': {
      const r = value as Relocation;
      if (r.scope === 'no') return 'No';
      if (r.scope === 'within_country') return 'Within their country';
      return r.countries?.length ? `Abroad: ${r.countries.map(countryName).join(', ')}` : 'Abroad';
    }
    case 'money': {
      const m = value as Money;
      return `${m.amount.toLocaleString('en-GB')} ${m.currency} a ${m.period}, ${m.includes}`;
    }
  }
}

// --- The company's questions — T123 (3 Oct 2026), the fit doc § 5 ---------------------------
//
// What kadmon keeps about each client company, so a person is set against the company as well as
// the role (Niv's "company character"). Mona's sheet, returned 29 Sep 2026, left the company tab as
// § 5.4 drafted it, so these are § 5.4 word for word: her sheet's question as `ask`, its "Why", and
// its "Could it reveal a confidential client?". A recruiter fills them on the company page; they
// live in `clients.character`, which the brain writes and candidate-vera reads (coupling 7).
//
// EVERY COMPANY QUESTION IS ONE AN AGENT MAY READ (§ 5.2): what the team wants kept goes in the
// pinned summary and the notes, which no agent reads. `identifying` is the one hold: on a
// confidential Search candidate-vera's context leaves those answers out, since a company's stage,
// size and offices can name it as surely as its name (§ 5.3), and so can anything a person types
// (Natanel, 3 Oct 2026: the two typed sentences too), which `questions.test.ts` holds.

/** `offices`: the countries, and the cities as a person writes them. */
export type Offices = { countries: string[]; cities?: string };

/** A company question's own types: a person's, bar `offices`, which only a company has. */
export type CompanyQuestionType = 'one_of' | 'many_of' | 'languages' | 'text' | 'offices';

export interface CompanyQuestion {
  /** Permanent, as a person's question's key is (§ 3.7); its own list, so `work_mode` is both. */
  key: string;
  label: string;
  /** The sheet's question, as a recruiter or (T123a) client-vera would ask it. */
  ask: string;
  /** The sheet's "Why". */
  why: string;
  type: CompanyQuestionType;
  options?: readonly QuestionOption[];
  /** Whether the answer could name a confidential client (§ 5.3): never on a confidential Search. */
  identifying: boolean;
  /** The QUESTIONS_VERSION that added it. */
  since: string;
  /** Kept for its answers, never asked (§ 3.7). */
  retired?: boolean;
}

export const COMPANY_QUESTIONS: readonly CompanyQuestion[] = [
  {
    key: 'stage',
    label: 'Stage',
    ask: 'What funding stage is the company at?',
    why: 'To match candidates who want a company at this stage.',
    type: 'one_of',
    options: [
      { key: 'pre_seed', label: 'Pre-seed' },
      { key: 'seed', label: 'Seed' },
      { key: 'series_a', label: 'Series A' },
      { key: 'series_b', label: 'Series B' },
      { key: 'series_c_plus', label: 'Series C or later' },
      { key: 'public', label: 'Public' },
      { key: 'bootstrapped', label: 'Bootstrapped' },
      { key: 'corporate_division', label: 'A corporate division' },
    ],
    identifying: true,
    since: V3,
  },
  {
    key: 'headcount',
    label: 'People',
    ask: 'How many people work at the company?',
    why: 'Tells candidates how big the company is, and matches people who have worked somewhere that size.',
    type: 'one_of',
    options: HEADCOUNT_BANDS,
    identifying: true,
    since: V3,
  },
  {
    key: 'eng_headcount',
    label: 'Engineers',
    ask: 'How many of them are engineers?',
    why: 'Tells engineering candidates how big the team they would join or lead is.',
    type: 'one_of',
    options: HEADCOUNT_BANDS,
    identifying: true,
    since: V3,
  },
  {
    key: 'offices',
    label: 'Where they are',
    ask: 'In which countries and cities does the company have offices?',
    why: 'To match candidates who live near an office or would move to one.',
    type: 'offices',
    identifying: true,
    since: V3,
  },
  {
    key: 'work_mode',
    label: 'How they work',
    ask: 'Do people work in the office, hybrid or remote?',
    why: 'To match candidates who want to work that way.',
    type: 'one_of',
    options: [
      { key: 'on_site', label: 'On site' },
      { key: 'hybrid', label: 'Hybrid' },
      { key: 'remote', label: 'Remote' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'pace',
    label: 'Pace',
    ask: 'How fast does the company move day to day?',
    why: 'To match candidates who do well at that pace.',
    type: 'one_of',
    options: [
      { key: 'steady', label: 'Steady' },
      { key: 'fast', label: 'Fast' },
      { key: 'very_fast', label: 'Very fast' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'decisions',
    label: 'How decisions get made',
    ask: 'How are decisions made: by the founder, by consensus, from data, or top-down?',
    why: 'To match candidates who work well in that kind of culture.',
    type: 'one_of',
    options: [
      { key: 'founder_led', label: 'Founder-led' },
      { key: 'consensus', label: 'By consensus' },
      { key: 'data', label: 'By data' },
      { key: 'top_down', label: 'Top-down' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'autonomy',
    label: 'How people are managed',
    ask: 'How closely are people managed: closely, with guidance, or left to own their work?',
    why: 'To match candidates to the amount of independence they want.',
    type: 'one_of',
    options: [
      { key: 'closely', label: 'Closely' },
      { key: 'with_guidance', label: 'With guidance' },
      { key: 'own_it', label: 'Left to own it' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'ai_maturity',
    label: 'AI in the company',
    ask: 'How much does the company use AI, from not yet to AI being the product?',
    why: 'To match candidates whose AI experience fits, and to set their expectations.',
    type: 'one_of',
    options: [
      { key: 'none_yet', label: 'None yet' },
      { key: 'experimenting', label: 'Experimenting' },
      { key: 'ai_first', label: 'AI-first' },
      { key: 'ai_product', label: 'AI is the product' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'work_language',
    label: 'Language at work',
    ask: 'Which languages do people work in day to day?',
    why: 'To match candidates who can work in them.',
    type: 'languages',
    identifying: false,
    since: V3,
  },
  {
    key: 'values',
    label: 'What they value most',
    ask: 'What does the company value most in its people? Pick a few.',
    why: 'To match candidates who share them.',
    type: 'many_of',
    options: [
      { key: 'ownership', label: 'Ownership' },
      { key: 'craft', label: 'Craft' },
      { key: 'speed', label: 'Speed' },
      { key: 'customers', label: 'Customers' },
      { key: 'research_depth', label: 'Research depth' },
      { key: 'frugality', label: 'Frugality' },
    ],
    identifying: false,
    since: V3,
  },
  {
    key: 'thrives',
    label: 'Who does well there',
    ask: 'In one sentence, what kind of person does well there?',
    why: 'Lets kadmon judge fit with the company, not only with the role.',
    type: 'text',
    // Natanel, 3 Oct 2026 ("Leave them out there"), over the sheet's "no": a sentence a person
    // types can name the client (a short name, a product, a founder, a city) in ways no check
    // catches, so on a confidential Search neither typed answer reaches candidate-vera.
    identifying: true,
    since: V3,
  },
  {
    key: 'struggles',
    label: 'Who struggles there',
    ask: 'In one sentence, what kind of person struggles there?',
    why: 'Lets kadmon spot people who would not last there.',
    type: 'text',
    // As `thrives`: Natanel, 3 Oct 2026, "Leave them out there".
    identifying: true,
    since: V3,
  },
];

const COMPANY_BY_KEY: ReadonlyMap<string, CompanyQuestion> = new Map(
  COMPANY_QUESTIONS.map((q) => [q.key, q]),
);

export function companyQuestionByKey(key: string): CompanyQuestion | undefined {
  return COMPANY_BY_KEY.get(key);
}

export type CompanyAnswerValue = string | string[] | Language[] | Offices;

/**
 * Where a company answer came from: the team's (`recruiter`), found online by the nightly lookup
 * (`looked_up`, T264, 4 Oct 2026), which never writes over the team's, or told by the company's own
 * people to client-vera (`client`, T123a, 4 Oct 2026), kept in the agent-owned
 * `company_told_answers` and set beside the card by {@link withToldAnswers}, never stored here.
 */
export const COMPANY_ANSWER_SOURCES = ['recruiter', 'looked_up', 'client'] as const;
export type CompanyAnswerSource = (typeof COMPANY_ANSWER_SOURCES)[number];

/** T264: the lookups a found answer comes from: Exa's pages read by Claude, then Coresignal's record. */
export const COMPANY_LOOKUPS = ['exa', 'coresignal'] as const;
export type CompanyLookup = (typeof COMPANY_LOOKUPS)[number];

/** One company answer as `clients.character` keeps it. */
export interface CompanyAnswer {
  value: CompanyAnswerValue;
  source: CompanyAnswerSource;
  /** ISO date-time it was set. */
  at: string;
  /** The staff member's name, on a team answer; the contact's, on a client's (T123a). */
  by?: string;
  /** A found answer's lookup (T264). */
  from?: CompanyLookup;
  /** A found answer's page, when one says it (T264). */
  url?: string;
}

/** The company's answers as read (`readCharacter`): key → the answer that stands. */
export type CompanyCharacter = Record<string, CompanyAnswer>;

/**
 * An answer the team cleared (T264 review round 1, finding 5): no value, so nothing reads it as an
 * answer, and the team's, so the nightly lookup never fills that question again. Natanel, 4 Oct
 * 2026, "Any clear stays empty" (T123c): a team answer cleared leaves the mark too, not only a
 * found one, so whatever the team clears stays empty until the team fills it in again.
 */
export interface ClearedCompanyAnswer {
  source: 'recruiter';
  cleared: true;
  at: string;
  by?: string;
}

/** `clients.character` as stored: key → the answer that stands, or the mark a cleared one left. */
export type StoredCharacter = Record<string, CompanyAnswer | ClearedCompanyAnswer>;

/**
 * A company answer checked against its question: the stored form, or why it cannot be one. A
 * language at work carries no level (a level describes a person); `offices` needs a country, since
 * a city alone sets nobody against it, and keeps the cities as written.
 */
export function parseCompanyAnswer(
  question: CompanyQuestion,
  raw: unknown,
): Parsed<CompanyAnswerValue> {
  const { type } = question;
  if (type === 'offices') {
    const entry = raw as { countries?: unknown; cities?: unknown } | null;
    const countries = parseAnswerValue(
      { type: 'countries', label: question.label },
      entry?.countries,
    );
    if (!countries.ok) return no('at least one country, such as SE or GB');
    if (entry?.cities === undefined || entry.cities === null || entry.cities === '') {
      return ok({ countries: countries.value as string[] });
    }
    const cities = parseAnswerValue({ type: 'text', label: question.label }, entry.cities);
    return cities.ok
      ? ok({ countries: countries.value as string[], cities: cities.value as string })
      : cities;
  }
  if (type !== 'languages') {
    return parseAnswerValue({ ...question, type }, raw) as Parsed<CompanyAnswerValue>;
  }
  // A company's languages carry no level, so a level sent is set aside unread, and the refusal
  // never asks for one (T123 review round 1, (9); T123c).
  if (!Array.isArray(raw) || raw.length === 0) return no('a list of languages, such as en or sv');
  const codes = raw.map((item) => ({ code: (item as { code?: unknown } | null)?.code }));
  const parsed = parseAnswerValue({ ...question, type }, codes);
  return parsed as Parsed<CompanyAnswerValue>;
}

/**
 * A refused company answer as the company card's route and its save both say it, "Stage: not one
 * of Stage's options." (T123 review round 1, (8); T123c): the question's label, or the key sent
 * when it is no question.
 */
export function companyRefusal(key: string, reason: string): string {
  return `${companyQuestionByKey(key)?.label ?? key}: ${reason}.`;
}

/** A company answer as a person reads it: "Sweden, Germany (Stockholm, Berlin)". */
export function describeCompanyAnswer(
  question: CompanyQuestion,
  value: CompanyAnswerValue,
): string {
  const { type } = question;
  if (type === 'offices') {
    const offices = value as Offices;
    const countries = offices.countries.map(countryName).join(', ');
    return offices.cities ? `${countries} (${offices.cities})` : countries;
  }
  return describeAnswer({ ...question, type }, value as AnswerValue);
}

/**
 * What a stored `clients.character` holds that still reads: each answer to a company question kadmon
 * keeps whose value its question still accepts, in the list's order. A key no longer on the list or a
 * value its question refuses is left out, never shown or rendered as something it is not.
 */
export function readCharacter(
  raw: unknown,
): { question: CompanyQuestion; answer: CompanyAnswer }[] {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) return [];
  const stored = raw as Record<string, Partial<CompanyAnswer> | null>;
  const out: { question: CompanyQuestion; answer: CompanyAnswer }[] = [];
  for (const question of COMPANY_QUESTIONS) {
    const answer = stored[question.key];
    if (!answer || typeof answer !== 'object' || typeof answer.at !== 'string') continue;
    const parsed = parseCompanyAnswer(question, answer.value);
    if (!parsed.ok) continue;
    const lookedUp = answer.source === 'looked_up';
    out.push({
      question,
      answer: {
        value: parsed.value,
        source: lookedUp ? 'looked_up' : answer.source === 'client' ? 'client' : 'recruiter',
        at: answer.at,
        ...(typeof answer.by === 'string' ? { by: answer.by } : {}),
        ...(lookedUp && COMPANY_LOOKUPS.includes(answer.from as CompanyLookup)
          ? { from: answer.from }
          : {}),
        ...(lookedUp && typeof answer.url === 'string' ? { url: answer.url } : {}),
      },
    });
  }
  return out;
}

/** What a save of the company card carries: key → the answer to set, or null to clear it. */
export type CharacterPatch = Record<string, unknown>;

/**
 * A save on top of what stands, as the brain writes it: each answer checked against its question
 * and stamped with who and when, the team's, each null a cleared answer: any answer cleared leaves
 * the team's mark (`ClearedCompanyAnswer`), so the night does not put one back (T123c), while a
 * mark stays as it was and a question with nothing stored stays with nothing. Every key the save does not name is kept EXACTLY as stored, a found
 * answer's source and page included (T264), so one team save never relabels what the nightly
 * lookup found as the team's. A key that is no company question, a retired one, or a value its
 * question refuses refuses the whole save, naming the question, so nothing half-saves.
 */
export function applyCharacterPatch(
  standing: unknown,
  patch: CharacterPatch,
  who: { by: string; at: string },
  /**
   * T123a: the questions a client's people answered (`company_told_answers`), which the card shows
   * though nothing is stored here: a clear of one leaves the team's mark, so the told answer stops
   * showing until the client tells it again.
   */
  told: ReadonlySet<string> = new Set(),
): { ok: true; value: StoredCharacter } | { ok: false; key: string; reason: string } {
  const out: StoredCharacter =
    standing && typeof standing === 'object' && !Array.isArray(standing)
      ? { ...(standing as StoredCharacter) }
      : {};
  for (const [key, raw] of Object.entries(patch)) {
    const question = companyQuestionByKey(key);
    if (!question || question.retired) {
      return { ok: false, key, reason: 'not one of the company questions' };
    }
    if (raw === null) {
      const was: unknown = out[key];
      if (was === undefined && !told.has(key)) continue;
      // Only these paths write the column, but a stored scalar must not throw `in` (T264 c2.7).
      // A mark stays as it was, unless a client's answer stands on it: then a fresh clear restamps
      // it, so what they told after the first clear goes too (T123a review round 1, finding 2).
      if (
        typeof was === 'object' &&
        was !== null &&
        'cleared' in was &&
        was.cleared === true &&
        !told.has(key)
      ) {
        continue;
      }
      out[key] = { source: 'recruiter', cleared: true, at: who.at, by: who.by };
      continue;
    }
    const parsed = parseCompanyAnswer(question, raw);
    if (!parsed.ok) return { ok: false, key, reason: parsed.reason };
    out[key] = { value: parsed.value, source: 'recruiter', at: who.at, by: who.by };
  }
  return { ok: true, value: out };
}

/** T123a: one answer a client's people told client-vera, as `company_told_answers` keeps it. */
export interface ToldCompanyAnswer {
  key: string;
  value: unknown;
  /** ISO date-time it was told. */
  at: string;
  /** The contact who told it, as the card names them. */
  by: string;
}

/**
 * T123a (4 Oct 2026, the fit doc § 5.5): the company's answers with what its own people told
 * client-vera set beside them, in the stored shape `readCharacter` reads. The team's own answer
 * always stands; a client's stands over one found online (Natanel, 4 Oct 2026: "The client's");
 * after the team cleared a question, a client's answer told later fills it again and one told
 * before stays cleared ("Client's answer shows"). A told key no longer asked, or a value its
 * question refuses, is left out.
 */
export function withToldAnswers(
  stored: unknown,
  told: readonly ToldCompanyAnswer[],
): StoredCharacter {
  const out: StoredCharacter =
    stored && typeof stored === 'object' && !Array.isArray(stored)
      ? { ...(stored as StoredCharacter) }
      : {};
  for (const answer of told) {
    const question = companyQuestionByKey(answer.key);
    if (!question || question.retired) continue;
    const parsed = parseCompanyAnswer(question, answer.value);
    if (!parsed.ok) continue;
    const standing: unknown = out[answer.key];
    if (standing && typeof standing === 'object') {
      const was = standing as Partial<CompanyAnswer> & { cleared?: unknown };
      if (was.cleared === true) {
        if (typeof was.at === 'string' && Date.parse(answer.at) <= Date.parse(was.at)) continue;
      } else if (was.source !== 'looked_up' && was.source !== 'client') {
        continue;
      }
    }
    out[answer.key] = { value: parsed.value, source: 'client', at: answer.at, by: answer.by };
  }
  return out;
}
